import React from 'react';
import { IconButton, IconButtonProps, Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import useModuleAccess from '../../hooks/useModuleAccess';

// `action` is omitted from IconButtonProps because MUI's ButtonBase already uses
// that name for an imperative-handle ref; here it means the permission verb.
interface PermissionIconButtonProps extends Omit<IconButtonProps, 'action' | 'title'> {
    /** Registry module key the action belongs to, e.g. 'buyers', 'suppliers'. */
    module: string;
    /** Action required, e.g. 'edit', 'create', 'delete'. */
    action: string;
    /** Optional sub-module, when the grant can be scoped finer than the module. */
    subModule?: string | null;
    /** Tooltip shown when the user IS allowed. Omit for no tooltip. */
    title?: React.ReactNode;
    /** Overrides the default "you don't have permission" tooltip. */
    deniedMessage?: string;
}

/**
 * An icon button that stays VISIBLE but goes disabled with a small lock badge
 * when the user lacks the permission — the icon-button counterpart of
 * PermissionMenuItem, used in row Actions cells.
 *
 * The click handler is dropped in the denied state so a disabled button can
 * never fire, and the button is wrapped in a span because a disabled MUI
 * IconButton has pointer-events: none and would otherwise swallow its tooltip.
 */
export const PermissionIconButton: React.FC<PermissionIconButtonProps> = ({
    module,
    action,
    subModule = null,
    title,
    deniedMessage,
    children,
    ...iconButtonProps
}) => {
    const { can } = useModuleAccess();

    if (can(module, action, subModule)) {
        const button = <IconButton {...iconButtonProps}>{children}</IconButton>;
        return title ? <Tooltip title={title}>{button}</Tooltip> : button;
    }

    const { onClick, style, ...allowedProps } = iconButtonProps;

    // Grey out the caller's inline colours so the button reads as unavailable.
    // These lists style their buttons with inline `style`, which would otherwise
    // beat MUI's own disabled colours.
    const disabledStyle: React.CSSProperties = {
        ...style,
        color: '#9e9e9e',
        backgroundColor: '#f5f5f5',
        border: '0.1px solid #d0d0d0',
    };

    return (
        <Tooltip arrow title={deniedMessage || `You do not have permission to ${action} in ${module}`}>
            <span style={{ display: 'inline-flex' }}>
                <IconButton
                    {...allowedProps}
                    style={{ ...disabledStyle, position: 'relative' }}
                    disabled
                    // Child icons often carry an explicit palette prop
                    // (color="info"/"success"/"primary"). That wins over the
                    // button's disabled colour, so a locked button would still
                    // render a bright blue/green icon and look clickable.
                    sx={{ '& .MuiSvgIcon-root': { color: '#9e9e9e' } }}
                >
                    {children}
                    {/* Sits inside the button box (not a Badge) so it can never be
                        clipped by the DataGrid cell's overflow: hidden. */}
                    <LockIcon
                        sx={{
                            position: 'absolute',
                            right: 0,
                            bottom: 0,
                            fontSize: 12,
                            // Beats the grey-out rule above — the lock stays legible.
                            color: '#4b5563 !important',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '50%',
                        }}
                    />
                </IconButton>
            </span>
        </Tooltip>
    );
};

export default PermissionIconButton;
